import { useMemo, useState } from "react";
import { useT, translateError } from "../../i18n/hooks.ts";
import { subBasinCentroid, subBasinGeometry, type SubBasinLayer } from "../../model/gisLayer.ts";
import {
  updateSubBasinColumn,
  assignOutletAquiferInBulk,
  assignSurfaceOutletInBulk,
  assignZoneInBulk,
  deleteSubBasinSeriesColumn,
  editSubBasin,
  deleteSubBasin,
} from "../../model/projectEntities.ts";
import type { AemetProvenance } from "../../model/storage.ts";
import type { SubBasin, EvalHidProject } from "../../model/types.ts";
import { AemetProvenanceDialog } from "../aemet/AemetProvenanceDialog.tsx";
import { CloudDownloadIcon } from "../CloudDownloadIcon.tsx";
import { Dialog } from "../Dialog.tsx";
import { ConfirmDeleteDialog } from "../ConfirmDeleteDialog.tsx";
import { ReportIcon } from "../ReportIcon.tsx";
import { TrashIcon } from "../TrashIcon.tsx";
import { useAemetFetch } from "../aemet/useAemetFetch.ts";
import { PasteDailySeries } from "../PasteDailySeries.tsx";
import { isSeriesScaleLocked, type ProjectSeries } from "../../model/dailySeries.ts";
import type { SeriesScale } from "../../model/seriesScale.ts";

/** A "— unassigned —" dropdown + options, for zone/snow zone/outlet/aquifer — same pattern for all four. */
function FieldSelector({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: number | null;
  options: { value: number; label: string }[];
  onChange: (value: number | null) => void;
}) {
  const t = useT();
  return (
    <div className="assigner-controls">
      <label>{label}</label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">{t("common.unassigned")}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

type Tab = "basics" | "series";
type SubBasinSeries = "precipitation" | "etp" | "temperature" | "pumping";

/**
 * Sub-basin editing dialog — two tabs: "Basic data" (name, area,
 * adjustments, zone/snow zone/outlet point/aquifer) and "Series" (view/
 * paste precipitation, ETP, temperature and pumping, one at a time via its
 * own selector — pumping shares the same underlying `Ent_Bomb.csv`/
 * `series.pumping` as the sub-basin's assigned aquifer (EditAquiferDialog's
 * own pumping tab), but resolved independently by the sub-basin's OWN name,
 * see `SubBasin.pumpingCol`'s own doc comment). The "Basic data" fields stay
 * in local state until "Save" is
 * pressed (a single project change, not one per field); series are applied
 * immediately when pasted, with their own flow (see PasteDailySeries.tsx) —
 * "Save"/"Cancel" don't affect them. Delete (permanent, with confirmation —
 * see projectEntities.ts) stays immediate: it's an action in itself, not a form
 * field.
 *
 * Shared by EditBar.tsx, SubBasinsPanel.tsx and SubBasinCanvas.tsx: the
 * three places from which "edit this sub-basin" can be reached.
 */
export function EditSubBasinDialog({
  project,
  subBasin,
  series,
  gisLayer,
  aemetProvenance,
  onRecordAemetProvenance,
  onChangeProject,
  onChangeSeries,
  onClose,
}: {
  project: EvalHidProject;
  subBasin: SubBasin;
  series: ProjectSeries;
  /** Needed to locate the sub-basin's geometry for "Fetch from AEMET" (nearest weather station) — undefined/null: that button stays disabled. */
  gisLayer?: SubBasinLayer | null;
  /** This sub-basin's last successful AEMET fetch, if any — shown next to the button so the source stays checkable, not just right after fetching. */
  aemetProvenance?: AemetProvenance;
  onRecordAemetProvenance?: (subBasinCode: number, provenance: AemetProvenance) => void;
  onChangeProject: (project: EvalHidProject) => void;
  onChangeSeries: (series: ProjectSeries) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [tab, setTab] = useState<Tab>("basics");
  const [seriesView, setSeriesView] = useState<SubBasinSeries>("precipitation");

  const centroid = useMemo(
    () => (gisLayer ? subBasinCentroid(gisLayer, subBasin.subBasinCode) : null),
    [gisLayer, subBasin.subBasinCode]
  );
  // Only used by the "rocio" AEMET method (point-in-polygon cell selection,
  // see aemetJobStore.ts's `resolveCandidates`) — every other method only
  // ever needed the centroid above.
  const geometry = useMemo(
    () => (gisLayer ? subBasinGeometry(gisLayer, subBasin.subBasinCode) : null),
    [gisLayer, subBasin.subBasinCode]
  );
  // A view over this sub-basin's job in aemetJobStore.ts (module-level, not
  // owned by this dialog) — only used here to START a job (and to show its
  // past-provenance icon). The small AemetFetchDialog.tsx itself is no
  // longer rendered from this component — see AemetFetchDialogHost.tsx —
  // precisely so a download survives closing this whole dialog, not just
  // its own small modal (see aemetJobStore.ts's header comment).
  const aemet = useAemetFetch({
    subBasinCode: subBasin.subBasinCode,
    subBasinName: subBasin.description,
    series,
    centroid,
    geometry,
  });
  const [description, setDescription] = useState(subBasin.description);
  const [area, setArea] = useState(String(subBasin.area));
  const [precipAdjustment, setPrecipAdjustment] = useState(String(subBasin.precipAdjustment));
  const [etpAdjustment, setEtpAdjustment] = useState(String(subBasin.etpAdjustment));
  const [tempAdjustment, setTempAdjustment] = useState(String(subBasin.tempAdjustment));
  const [zone, setZone] = useState(subBasin.zoneCode);
  const [snowZone, setSnowZone] = useState(subBasin.snowZoneCode);
  const [outlet, setOutlet] = useState(subBasin.outletCode);
  const [aquifer, setAquifer] = useState(subBasin.aquiferCode === 0 ? null : subBasin.aquiferCode);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [showAemetReport, setShowAemetReport] = useState(false);
  const codes = new Set([subBasin.subBasinCode]);
  const scaleLocked = isSeriesScaleLocked(series);

  /** Folds an inferred scale (from the FIRST series ever pasted into this project) into the same project write as `changeColumn` below — see PasteDailySeries.tsx's own `onIndexChange` doc comment on why these can't be two separate `onChangeProject` calls. */
  function changeColumn(
    field: "precipCol" | "etpCol" | "tempCol" | "pumpingCol",
    index: number,
    inferredScale?: SeriesScale
  ) {
    let p = updateSubBasinColumn(project, subBasin.subBasinCode, field, index);
    if (inferredScale) p = { ...p, seriesScale: inferredScale };
    onChangeProject(p);
  }

  function save() {
    try {
      let p = editSubBasin(project, subBasin.subBasinCode, {
        description,
        area: Number(area) || 0,
        // Web engine's partial results: always available (see the detail in the
        // results panel), independent of this .ehd checkbox — see the entry in types.ts.
        partialResults: subBasin.partialResults === 1,
        precipAdjustment: Number(precipAdjustment) || 1,
        etpAdjustment: Number(etpAdjustment) || 1,
        tempAdjustment: Number(tempAdjustment) || 1,
      });
      p = assignZoneInBulk(p, codes, "zoneCode", zone);
      p = assignZoneInBulk(p, codes, "snowZoneCode", snowZone);
      p = assignSurfaceOutletInBulk(p, codes, outlet);
      p = assignOutletAquiferInBulk(p, codes, aquifer ?? 0);
      onChangeProject(p);
      onClose();
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  return (
    <Dialog
      title={t("editSubBasinDialog.title", { name: description })}
      onClose={onClose}
      size="wide"
    >
      <div className="dialog-tabs">
        <button
          className={tab === "basics" ? "tab-active" : undefined}
          onClick={() => setTab("basics")}
        >
          {t("editSubBasinDialog.basicData")}
        </button>
        <button
          className={tab === "series" ? "tab-active" : undefined}
          onClick={() => setTab("series")}
        >
          {t("common.series")}
        </button>
      </div>

      {tab === "basics" && (
        <>
          <div className="assigner-controls">
            <label>{t("common.name")}</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="assigner-controls">
            <label>{t("resultsPanel.areaKm2")}</label>
            <input type="number" value={area} onChange={(e) => setArea(e.target.value)} />
          </div>

          <div className="assigner-controls">
            <label>{t("editSubBasinDialog.precipitationAdjustment")}</label>
            <input
              type="number"
              step="0.01"
              value={precipAdjustment}
              onChange={(e) => setPrecipAdjustment(e.target.value)}
            />
          </div>
          <div className="assigner-controls">
            <label>{t("editSubBasinDialog.etpAdjustment")}</label>
            <input
              type="number"
              step="0.01"
              value={etpAdjustment}
              onChange={(e) => setEtpAdjustment(e.target.value)}
            />
          </div>
          <div className="assigner-controls">
            <label>{t("editSubBasinDialog.temperatureAdjustment")}</label>
            <input
              type="number"
              step="0.01"
              value={tempAdjustment}
              onChange={(e) => setTempAdjustment(e.target.value)}
            />
          </div>

          <FieldSelector
            label={t("resultsPanel.zone")}
            value={zone}
            options={project.zones
              .filter((z) => z.modelTypeCode != null)
              .map((z) => ({ value: z.zoneCode, label: z.name }))}
            onChange={setZone}
          />
          <FieldSelector
            label={t("subBasinsPanel.snowZone")}
            value={snowZone}
            options={project.zones
              .filter((z) => z.snowModelTypeCode != null)
              .map((z) => ({ value: z.zoneCode, label: z.name }))}
            onChange={setSnowZone}
          />
          <FieldSelector
            label={t("legend.outletPoint")}
            value={outlet}
            options={project.outletPoints.map((p) => ({
              value: p.outletCode,
              label: p.name,
            }))}
            onChange={setOutlet}
          />
          <FieldSelector
            label={t("legend.aquifer")}
            value={aquifer}
            options={project.aquifers.map((a) => ({
              value: a.aquiferCode,
              label: a.name,
            }))}
            onChange={setAquifer}
          />
        </>
      )}

      {tab === "series" && (
        <>
          <div className="dialog-tabs">
            {onRecordAemetProvenance && (
              <div className="aemet-fetch-bar">
                {aemetProvenance ? (
                  // Once there's a report to show, that replaces the fetch
                  // button entirely instead of sitting next to it — a fresh
                  // fetch would just redo the same forced date range
                  // (series already loaded), and "Retry N failed requests"
                  // inside the report already covers the one thing that
                  // could still be incomplete.
                  <button
                    className="button-icon"
                    title={t("editSubBasinDialog.viewAemetReport")}
                    aria-label={t("editSubBasinDialog.viewAemetReport")}
                    onClick={() => setShowAemetReport(true)}
                  >
                    <ReportIcon />
                  </button>
                ) : (
                  // Always offered whenever there's a centroid — hiding it
                  // whenever there was no AEMET STATION within reach used to
                  // be a safe shortcut ("guaranteed no stations found"
                  // otherwise), but that stopped being true once "rocio"
                  // (gridded reanalysis data, no physical station required)
                  // became a selectable method: a sub-basin far from any
                  // weather station can still have full ROCIO coverage. Real
                  // bug this fixes — a Duero-scale (1728 sub-basins) bulk
                  // ROCIO download silently skipped every sub-basin that
                  // failed this AEMET-only proximity check, 2026-08-27.
                  <button
                    className="button-icon"
                    disabled={!centroid}
                    title={
                      centroid
                        ? t("editSubBasinDialog.fetchFromAemet")
                        : t("editSubBasinDialog.aemetNoGeometry")
                    }
                    aria-label={t("editSubBasinDialog.fetchFromAemet")}
                    onClick={() => {
                      // Starts (or resumes, if one's already running/awaiting
                      // input for this sub-basin) the AEMET job and shows its
                      // dialog via AemetFetchDialogHost.tsx, then closes THIS
                      // dialog right away — the two stacked on top of each
                      // other made for a confusing screen, and nothing here
                      // is needed anymore for the fetch to run to completion.
                      aemet.start();
                      aemet.openDialog();
                      onClose();
                    }}
                  >
                    <CloudDownloadIcon />
                  </button>
                )}
              </div>
            )}
            <button
              className={seriesView === "precipitation" ? "tab-active" : undefined}
              onClick={() => setSeriesView("precipitation")}
            >
              {t("infoPopover.precipitation")}
            </button>
            <button
              className={seriesView === "etp" ? "tab-active" : undefined}
              onClick={() => setSeriesView("etp")}
            >
              {t("infoPopover.etp")}
            </button>
            <button
              className={seriesView === "temperature" ? "tab-active" : undefined}
              onClick={() => setSeriesView("temperature")}
            >
              {t("infoPopover.temperature")}
            </button>
            <button
              className={seriesView === "pumping" ? "tab-active" : undefined}
              onClick={() => setSeriesView("pumping")}
            >
              {t("infoPopover.pumping")}
            </button>
          </div>

          {seriesView === "precipitation" && (
            <PasteDailySeries
              title={t("infoPopover.precipitation")}
              showTitle={false}
              series={series.precipitation}
              columnName={subBasin.description}
              colIndex={subBasin.precipCol}
              scale={project.seriesScale}
              scaleLocked={scaleLocked}
              onSeriesChange={(next) => onChangeSeries({ ...series, precipitation: next })}
              onIndexChange={(index, inferredScale) =>
                changeColumn("precipCol", index, inferredScale)
              }
              onDeleteSeries={() => {
                const result = deleteSubBasinSeriesColumn(
                  project,
                  series.precipitation!,
                  subBasin.subBasinCode,
                  "precipCol"
                );
                return {
                  sharedWithOthers: result.sharedWithOthers,
                  confirm: () => {
                    onChangeProject(result.project);
                    onChangeSeries({ ...series, precipitation: result.series });
                  },
                };
              }}
            />
          )}
          {seriesView === "etp" && (
            <PasteDailySeries
              title={t("infoPopover.etp")}
              showTitle={false}
              series={series.etp}
              columnName={subBasin.description}
              colIndex={subBasin.etpCol}
              scale={project.seriesScale}
              scaleLocked={scaleLocked}
              onSeriesChange={(next) => onChangeSeries({ ...series, etp: next })}
              onIndexChange={(index, inferredScale) => changeColumn("etpCol", index, inferredScale)}
              onDeleteSeries={() => {
                const result = deleteSubBasinSeriesColumn(
                  project,
                  series.etp!,
                  subBasin.subBasinCode,
                  "etpCol"
                );
                return {
                  sharedWithOthers: result.sharedWithOthers,
                  confirm: () => {
                    onChangeProject(result.project);
                    onChangeSeries({ ...series, etp: result.series });
                  },
                };
              }}
            />
          )}
          {seriesView === "temperature" && (
            <PasteDailySeries
              title={t("infoPopover.temperature")}
              showTitle={false}
              series={series.temperature}
              columnName={subBasin.description}
              colIndex={subBasin.tempCol}
              scale={project.seriesScale}
              scaleLocked={scaleLocked}
              onSeriesChange={(next) => onChangeSeries({ ...series, temperature: next })}
              onIndexChange={(index, inferredScale) =>
                changeColumn("tempCol", index, inferredScale)
              }
              onDeleteSeries={() => {
                const result = deleteSubBasinSeriesColumn(
                  project,
                  series.temperature!,
                  subBasin.subBasinCode,
                  "tempCol"
                );
                return {
                  sharedWithOthers: result.sharedWithOthers,
                  confirm: () => {
                    onChangeProject(result.project);
                    onChangeSeries({ ...series, temperature: result.series });
                  },
                };
              }}
            />
          )}
          {seriesView === "pumping" && (
            <PasteDailySeries
              title={t("infoPopover.pumping")}
              showTitle={false}
              series={series.pumping}
              columnName={subBasin.description}
              colIndex={subBasin.pumpingCol}
              scale={project.seriesScale}
              scaleLocked={scaleLocked}
              onSeriesChange={(next) => onChangeSeries({ ...series, pumping: next })}
              onIndexChange={(index, inferredScale) =>
                changeColumn("pumpingCol", index, inferredScale)
              }
              onDeleteSeries={() => {
                const result = deleteSubBasinSeriesColumn(
                  project,
                  series.pumping!,
                  subBasin.subBasinCode,
                  "pumpingCol"
                );
                return {
                  sharedWithOthers: result.sharedWithOthers,
                  confirm: () => {
                    onChangeProject(result.project);
                    onChangeSeries({ ...series, pumping: result.series });
                  },
                };
              }}
            />
          )}
        </>
      )}

      {error && <p className="error">{error}</p>}

      <div className="dialog-actions">
        <button className="button button-primary" onClick={save}>
          {t("common.save")}
        </button>
        <button className="button" onClick={onClose}>
          {t("common.cancel")}
        </button>
        <button
          className="button-icon button-danger dialog-action-separate"
          title={t("subBasinsPanel.deletePermanentTitle")}
          onClick={() => setConfirmingDelete(true)}
        >
          <TrashIcon />
        </button>
      </div>

      {confirmingDelete && (
        <ConfirmDeleteDialog
          title={t("subBasinsPanel.deleteDialogTitle")}
          name={subBasin.description}
          onConfirm={() => {
            onChangeProject(deleteSubBasin(project, subBasin.subBasinCode));
            onClose();
          }}
          onClose={() => setConfirmingDelete(false)}
        />
      )}

      {showAemetReport && aemetProvenance && onRecordAemetProvenance && (
        <AemetProvenanceDialog
          provenance={aemetProvenance}
          columnName={subBasin.description}
          centroid={centroid}
          series={series}
          onChangeSeries={(trio) => onChangeSeries({ ...series, ...trio })}
          onChangeProvenance={(provenance) =>
            onRecordAemetProvenance(subBasin.subBasinCode, provenance)
          }
          onClose={() => setShowAemetReport(false)}
        />
      )}
    </Dialog>
  );
}
